import {
    ChangeDetectionStrategy,
    Component,
    Input,
    input,
    model,
    OnChanges,
    output,
    signal,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import {
    ReportBuilder,
    ReportBuilderCell,
    ReportBuilderCellVariable,
    ReportBuilderRow,
} from "./s25.document.report.builder.table.component";
import { Doc } from "./s25.document.const";
import { Bind } from "../../../decorators/bind.decorator";
import { DocumentUtil } from "./s25.document.util";
import { Document, DocumentService } from "../../../services/document.service";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder",
    template: `
        @if (!init()) {
            <s25-ng-loading-inline-static />
        } @else {
            <label class="reportBuilderLabel">Table Header</label>
            @if (reportBuilder().header?.length) {
                <s25-ng-document-report-builder-table
                    class="header"
                    [type]="type"
                    [(rows)]="reportBuilder().header"
                    [section]="$any('header')"
                    [breakpoints]="breakpoints.body"
                    [allowEmpty]="true"
                    [editable]="editable()"
                    (changed)="onChange()"
                />
            } @else {
                <s25-ng-button class="addHeaders" [type]="'outline'" (click)="addHeaders()">Add Headers</s25-ng-button>
            }

            <label class="reportBuilderLabel">Table Body</label>
            <s25-ng-document-report-builder-table
                class="body"
                [type]="type"
                [(rows)]="reportBuilder().body"
                [section]="$any('body')"
                [breakpoints]="breakpoints.header"
                [editable]="editable()"
                (changed)="onChange()"
            />
        }
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
        }

        .addHeaders {
            display: block;
        }

        label {
            margin-top: 1em;
        }

        :host.nested label,
        :host.nested s25-ng-button {
            display: none;
        }

        :host-context(s25-ng-document-report-builder-table) {
            .reportBuilderLabel,
            .addHeaders {
                display: none;
            }

            s25-ng-document-report-builder-table.header {
                border-bottom: var(--border-width) solid var(--border-color);
                display: block;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderComponent implements OnChanges {
    // Provide either document or document ID
    @Input() document: Document;
    @Input() documentId: number;

    @Input() type: Doc.ReportBuilderTypeId;
    editable = input<boolean>(true);
    reportBuilder = model<ReportBuilder>({
        variable: "ws.flexTable.reservations",
        header: [{ cells: new Array(4).fill(null).map((_) => DocumentUtil.newCell({ section: "header" })) }],
        body: [{ cells: new Array(4).fill(null).map((_) => DocumentUtil.newCell({ section: "body" })) }],
    });

    tableStrChange = output<string>();

    init = signal(false);
    tableStr: string = "";
    breakpoints: { header: number[]; body: number[] } = { header: [], body: [] };

    ngOnChanges(changes: SimpleChanges) {
        if (changes.document || changes.documentId) this.initialize();
        if (changes.type) this.onTypeChange();
    }

    async initialize() {
        this.init.set(false);
        let document: Document = this.document;
        if (!document) {
            document = await DocumentService.getDocument(this.documentId);
        }
        this.tableStr = document.body_text;
        if (!this.type) this.type = document.document_type as Doc.ReportBuilderTypeId;
        if (this.tableStr) this.reportBuilder.set(await DocumentUtil.parseReportBuilder(this.tableStr, this.type));
        else this.reportBuilder().variable = `ws.flexTable.${this.type}`;
        this.init.set(true);
        this.onChange();
    }

    onChange() {
        if (!this.editable()) return;

        this.breakpoints.header = DocumentUtil.getBreakpoints(this.reportBuilder().header);
        this.breakpoints.body = DocumentUtil.getBreakpoints(this.reportBuilder().body);
        this.stringify();
    }

    stringify() {
        const headerCells = this.reportBuilder().header.flatMap(this.stringifyRow).join(" || ");
        const bodyCells = this.reportBuilder()
            .body.flatMap((row, r) => this.stringifyRow(row, this.reportBuilder().header.length + r))
            .join(" || ");

        this.tableStr = `{{for: ws.flexTable.${this.type}: ${headerCells ? headerCells + " || " : ""}${bodyCells} :end-for}}`;
        this.tableStrChange.emit(this.tableStr);
    }

    @Bind
    stringifyRow(row: ReportBuilderRow, rowIndex: number) {
        return row.cells.map((cell) => this.stringifyCell(cell, rowIndex)).join(" || ");
    }

    stringifyCell(cell: ReportBuilderCell, row: number) {
        return `${cell.section()} ^ ${row + 1} ^ ${cell.label() ?? ""} ^ ${this.stringifyVariable(cell.variable()) ?? ""} ^ ${cell.css() ?? ""} ^ ${cell.width().toFixed(1)}`;
    }

    stringifyVariable(variable: ReportBuilderCellVariable) {
        if (variable.type() === "variable") return variable.state.var;
        if (variable.type() === "subreport") return variable.state.var;
        if (variable.type() === "function" && !!variable.state.variable) {
            const args = variable.arguments.map((arg) => {
                if (arg.type === "text") return arg.text;
                if (arg.type === "variable") return arg.state.var;
                return "";
            });
            return `${variable.state.variable.val}(${args.filter((arg) => arg).join(",")})`;
        }
        return "";
    }

    addHeaders() {
        this.reportBuilder().header = [
            {
                cells: this.reportBuilder().body[0].cells.map((cell) =>
                    DocumentUtil.newCell({
                        section: "header",
                        width: cell.width(),
                    }),
                ),
            },
        ];
        this.onChange();
    }

    onTypeChange() {
        // Clear variables
        for (const row of this.reportBuilder().header.concat(this.reportBuilder().body)) {
            for (const cell of row.cells) DocumentUtil.clearReportBuilderVariable(cell);
        }
        this.stringify();
    }

    validate() {
        const rows = this.reportBuilder().header.concat(this.reportBuilder().body);
        return rows.every((row) => {
            return row.cells.every((cell) => cell.valid());
        });
    }
}
